import { getIdToken } from './auth'
import type { ApiResponse, AuthenticatedUser, PaginatedResponse, Profile } from '@/types/api'
import type { Workflow } from '@/types/workflow'
import type {
  AdminOrganizationDetail,
  AdminOrganizationListItem,
  AdminWorkflow,
  AdminWorkflowBlockSettings,
  AdminWorkflowEmailSettings,
  AdminWorkflowListItem,
  AdminWorkflowErpSettings,
  WorkflowAudience,
  WorkflowFlow,
  WorkflowStatus,
  WorkflowStartMode,
} from '@/types/admin'
import type { PendingList, DoneList } from '@/types/purchaseOrder'
import type { PurchaseOrderValidation } from '@/types/purchaseOrderValidation'
import type { ToDoList } from '@/types/workflowValidation'
import type {
  AckResponse,
  OrderConfirmationDetail,
  PurchaseOrderSearchResults,
  ValidateAckResponse,
} from '@/types/orderConfirmationDetail'
import type { InboxEmailDetail, InboxEmailMeta, InboxItem } from '@/types/inbox'
import type {
  CustomerOrderCounts,
  ArticleInventory,
  ArticleSuggestion,
  CustomerDetail,
  CustomerList,
  CustomerOrderDetail,
  CreateSalesOrderResult,
  CustomerOrderDraft,
  NewCustomerOrderRow,
  CustomerSuggestion,
  CustomerOrderList,
  SalesOrderInboxEmailDetail,
  SalesOrderInboxItem,
} from '@/types/salesOrder'
import type {
  OrderConfirmationSettings,
  UpdateOrderConfirmationSettingsRequest,
} from '@/types/orderConfirmationSettings'
import type {
  SalesOrderSettings,
  UpdateSalesOrderSettingsRequest,
} from '@/types/salesOrderSettings'
import type { SupplierList } from '@/types/suppliers'
import { getActingAs } from '@/state/actingAs'

const API_BASE_URL = import.meta.env.VITE_API_GATEWAY_URL || ''

/**
 * Custom error class for API errors
 */
export class ApiRequestError extends Error {
  constructor(
    message: string,
    public status?: number,
    public code?: string,
    public erpErrors?: string[],
  ) {
    super(message)
    this.name = 'ApiRequestError'
  }
}

/**
 * Makes an authenticated request to the API Gateway
 */
async function makeRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  async function performRequest(forceRefreshToken: boolean): Promise<Response> {
    const token = await getIdToken(forceRefreshToken)

    if (!token) {
      throw new ApiRequestError('Not authenticated', 401, 'UNAUTHORIZED')
    }

    const actingAs = getActingAs()

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(actingAs ? { 'X-Act-As-Organization-Id': actingAs.organizationId } : {}),
      ...options.headers,
    }

    return fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    })
  }

  try {
    if (!API_BASE_URL) {
      throw new ApiRequestError(
        'API base URL is not configured (VITE_API_GATEWAY_URL).',
        500,
        'API_BASE_URL_MISSING'
      )
    }
    let response = await performRequest(false)
    // If token expired/stale, retry once with forced refresh.
    if (response.status === 401) {
      response = await performRequest(true)
    }

    // Handle non-OK responses
    if (!response.ok) {
      const raw = await response.text()
      let errorData: { message?: string; code?: string; error?: string; erp_errors?: string[] } = {}
      if (raw) {
        try {
          errorData = JSON.parse(raw) as {
            message?: string
            code?: string
            error?: string
            erp_errors?: string[]
          }
        } catch {
          // Non-JSON error body; keep raw for message fallback.
        }
      }
      const apiMessage = errorData.message || errorData.error
      const erpErrors = errorData.erp_errors?.map((e) => e.trim()).filter(Boolean)
      throw new ApiRequestError(
        apiMessage || raw || `HTTP ${response.status}: ${response.statusText}`,
        response.status,
        errorData.code,
        erpErrors?.length ? erpErrors : undefined,
      )
    }

    // Parse JSON responses; return text for non-JSON responses.
    const contentType = response.headers.get('content-type') || ''
    if (contentType.includes('application/json')) {
      const envelope = await response.json() as ApiResponse<T>
      return envelope.data
    }
    return (await response.text()) as T
  } catch (error) {
    if (error instanceof ApiRequestError) {
      throw error
    }
    
    // Handle network errors
    if (error instanceof TypeError) {
      throw new ApiRequestError(
        'Network error. Please check your connection.',
        0,
        'NETWORK_ERROR'
      )
    }

    throw new ApiRequestError(
      'An unexpected error occurred',
      500,
      'UNKNOWN_ERROR'
    )
  }
}

/**
 * API client with common HTTP methods
 */
export const api = {
  /**
   * GET request
   */
  get: <T>(endpoint: string): Promise<T> => {
    return makeRequest<T>(endpoint, {
      method: 'GET',
    })
  },

  /**
   * POST request
   */
  post: <T>(endpoint: string, data?: unknown): Promise<T> => {
    return makeRequest<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  /**
   * PUT request
   */
  put: <T>(endpoint: string, data?: unknown): Promise<T> => {
    return makeRequest<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  /**
   * PATCH request
   */
  patch: <T>(endpoint: string, data?: unknown): Promise<T> => {
    return makeRequest<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  /**
   * DELETE request
   */
  delete: <T>(endpoint: string): Promise<T> => {
    return makeRequest<T>(endpoint, {
      method: 'DELETE',
    })
  },
}

export async function getAuthenticatedUserProfile(): Promise<Profile> {
  return api.get<Profile>('/v1/users/me')
}

/** Ensures the Cognito user exists in the database (used during sign-in). */
export async function ensureAuthenticatedUserInDatabase(): Promise<void> {
  await getAuthenticatedUserProfile()
}

export async function createAuthenticatedUser(): Promise<AuthenticatedUser> {
  return api.post<AuthenticatedUser>('/v1/users/me')
}

export async function getWorkflows(): Promise<Workflow[]> {
  return api.get<Workflow[]>('/v1/workflows')
}

export interface ListParams {
  page?: number
  purchasers?: string[]
  suppliers?: string[]
  q?: string
}

function buildListParams(params: ListParams = {}): string {
  const qs = new URLSearchParams()
  if (params.page && params.page > 1) qs.set('page', String(params.page))
  params.purchasers?.forEach((p) => qs.append('purchaser', p))
  params.suppliers?.forEach((s) => qs.append('supplier', s))
  if (params.q) qs.set('q', params.q)
  const str = qs.toString()
  return str ? `?${str}` : ''
}

export async function getWorkflowToDo(
  workflowId: string,
  params?: ListParams,
): Promise<ToDoList> {
  return api.get<ToDoList>(`/v1/workflows/${workflowId}/todo${buildListParams(params)}`)
}

export async function getWorkflowPending(
  workflowId: string,
  params?: ListParams,
): Promise<PendingList> {
  return api.get<PendingList>(`/v1/workflows/${workflowId}/pending${buildListParams(params)}`)
}

export async function getWorkflowDone(
  workflowId: string,
  params?: ListParams,
): Promise<DoneList> {
  return api.get<DoneList>(`/v1/workflows/${workflowId}/done${buildListParams(params)}`)
}

export interface WorkflowCounts {
  todo: number
  pending: number
}

export async function getWorkflowCounts(workflowId: string): Promise<WorkflowCounts> {
  return api.get<WorkflowCounts>(`/v1/workflows/${workflowId}/counts`)
}

export async function getWorkflowOrderConfirmationSettings(
  workflowId: string,
): Promise<OrderConfirmationSettings> {
  return api.get<OrderConfirmationSettings>(`/v1/workflows/${workflowId}/settings`)
}

export async function saveWorkflowOrderConfirmationSettings(
  workflowId: string,
  body: UpdateOrderConfirmationSettingsRequest,
): Promise<OrderConfirmationSettings> {
  return api.post<OrderConfirmationSettings>(`/v1/workflows/${workflowId}/settings`, body)
}

export async function getWorkflowSuppliers(workflowId: string): Promise<SupplierList> {
  return api.get<SupplierList>(`/v1/workflows/${workflowId}/suppliers`)
}

export async function getSupplierSettings(
  workflowId: string,
  supplierId: string,
): Promise<OrderConfirmationSettings> {
  return api.get<OrderConfirmationSettings>(
    `/v1/workflows/${workflowId}/suppliers/${supplierId}/settings`,
  )
}

export async function saveSupplierSettings(
  workflowId: string,
  supplierId: string,
  body: UpdateOrderConfirmationSettingsRequest,
): Promise<OrderConfirmationSettings> {
  return api.post<OrderConfirmationSettings>(
    `/v1/workflows/${workflowId}/suppliers/${supplierId}/settings`,
    body,
  )
}

export async function deleteSupplierSettings(
  workflowId: string,
  supplierId: string,
): Promise<void> {
  await api.delete<void>(`/v1/workflows/${workflowId}/suppliers/${supplierId}/settings`)
}

export async function getPurchaseOrderValidation(purchaseOrderId: string): Promise<PurchaseOrderValidation> {
  return api.get<PurchaseOrderValidation>(`/v1/purchase-orders/${purchaseOrderId}`)
}

export async function syncPurchaseOrderLatestVersion(purchaseOrderId: string): Promise<AckResponse> {
  return api.post<AckResponse>(`/v1/purchase-orders/${purchaseOrderId}/sync`)
}

export async function getInbox(workflowId: string, page?: number): Promise<PaginatedResponse<InboxItem>> {
  const qs = page && page > 1 ? `?page=${page}` : ''
  return api.get<PaginatedResponse<InboxItem>>(`/v1/workflows/${workflowId}/inbox${qs}`)
}

export async function getInboxEmail(workflowId: string, emailId: string): Promise<InboxEmailDetail> {
  return api.get<InboxEmailDetail>(`/v1/workflows/${workflowId}/inbox/${emailId}`)
}

export async function getInboxEmailMeta(workflowId: string, emailId: string): Promise<InboxEmailMeta> {
  return api.get<InboxEmailMeta>(`/v1/workflows/${workflowId}/inbox/${emailId}/meta`)
}

/* ---------------------------------------------------------------------------
 * Sales order
 *
 * A workflow is either an order confirmation or a sales order flow, never both, so the sales order
 * reads sit under their own prefix rather than changing what the shared workflow routes return.
 * All of these are read-only.
 * ------------------------------------------------------------------------ */

export async function getSalesOrderInbox(
  workflowId: string,
  page?: number,
): Promise<PaginatedResponse<SalesOrderInboxItem>> {
  const qs = page && page > 1 ? `?page=${page}` : ''
  return api.get<PaginatedResponse<SalesOrderInboxItem>>(
    `/v1/workflows/${workflowId}/sales-order/inbox${qs}`,
  )
}

export async function getSalesOrderInboxEmail(
  workflowId: string,
  emailId: string,
): Promise<SalesOrderInboxEmailDetail> {
  return api.get<SalesOrderInboxEmailDetail>(
    `/v1/workflows/${workflowId}/sales-order/inbox/${emailId}`,
  )
}

export async function getSalesOrderInboxEmailMeta(
  workflowId: string,
  emailId: string,
): Promise<InboxEmailMeta> {
  return api.get<InboxEmailMeta>(
    `/v1/workflows/${workflowId}/sales-order/inbox/${emailId}/meta`,
  )
}

export interface CustomerOrderListParams {
  page?: number
  customers?: string[]
  q?: string
}

function buildCustomerOrderListParams(params: CustomerOrderListParams = {}): string {
  const qs = new URLSearchParams()
  if (params.page && params.page > 1) qs.set('page', String(params.page))
  params.customers?.forEach((c) => qs.append('customer', c))
  if (params.q) qs.set('q', params.q)
  const str = qs.toString()
  return str ? `?${str}` : ''
}

export async function getCustomerOrdersAwaitingValidation(
  workflowId: string,
  params?: CustomerOrderListParams,
): Promise<CustomerOrderList> {
  return api.get<CustomerOrderList>(
    `/v1/workflows/${workflowId}/sales-order/customer-orders/awaiting-validation${buildCustomerOrderListParams(params)}`,
  )
}

export async function getCustomerOrdersDone(
  workflowId: string,
  params?: CustomerOrderListParams,
): Promise<CustomerOrderList> {
  return api.get<CustomerOrderList>(
    `/v1/workflows/${workflowId}/sales-order/customer-orders/done${buildCustomerOrderListParams(params)}`,
  )
}

export async function getSalesOrderCounts(workflowId: string): Promise<CustomerOrderCounts> {
  return api.get<CustomerOrderCounts>(`/v1/workflows/${workflowId}/sales-order/counts`)
}

/**
 * Where a suggestion comes from.
 *
 * `local` reads the workflow's own tables and answers in milliseconds. `erp` goes out to the
 * ERP and records what it finds, which is what fills those tables in the first place. The
 * typeahead asks for both so the ERP is never between a keystroke and the first suggestion.
 */
export type SuggestionSource = 'local' | 'erp'

/**
 * Article suggestions for what has been typed.
 *
 * The ERP source can reach an article the pipeline has never matched. Only Jeeves implements
 * it; other providers answer with an error rather than an empty list, so "nothing found" stays
 * distinguishable from "this ERP cannot be searched".
 */
export async function searchErpArticles(
  workflowId: string,
  query: string,
  source: SuggestionSource,
): Promise<ArticleSuggestion[]> {
  return api.get<ArticleSuggestion[]>(
    `/v1/workflows/${workflowId}/erp/articles?q=${encodeURIComponent(query)}&source=${source}`,
  )
}

/**
 * Stock for the given articles, read from the ERP on every call.
 *
 * Kept separate from the customer order so the order renders while this is still in flight, and
 * so an article picked in the typeahead can be asked about on its own.
 */
export async function getErpInventory(
  workflowId: string,
  articleNumbers: string[],
): Promise<ArticleInventory[]> {
  return api.get<ArticleInventory[]>(
    `/v1/workflows/${workflowId}/erp/inventory?article_numbers=${encodeURIComponent(articleNumbers.join(','))}`,
  )
}

/** Customer suggestions for what has been typed. See searchErpArticles. */
export async function searchErpCustomers(
  workflowId: string,
  query: string,
  source: SuggestionSource,
): Promise<CustomerSuggestion[]> {
  return api.get<CustomerSuggestion[]>(
    `/v1/workflows/${workflowId}/erp/customers?q=${encodeURIComponent(query)}&source=${source}`,
  )
}

/**
 * Everything we hold about one customer, including the addresses and contacts that hang off them.
 *
 * What the validation view asks when somebody points an order at a different company: the order's
 * own detail already carries the matched customer, and this is the same answer for one it has not
 * been saved against yet.
 */
export async function getCustomerDetail(
  workflowId: string,
  customerId: string,
): Promise<CustomerDetail> {
  return api.get<CustomerDetail>(
    `/v1/workflows/${workflowId}/sales-order/customers/${customerId}`,
  )
}

/** The customers a sales order workflow has matched orders to. */
export async function getSalesOrderCustomers(
  workflowId: string,
  page: number,
): Promise<CustomerList> {
  return api.get<CustomerList>(
    `/v1/workflows/${workflowId}/sales-order/customers?page=${page}`,
  )
}

export async function getCustomerOrderDetail(
  customerOrderId: string,
): Promise<CustomerOrderDetail> {
  return api.get<CustomerOrderDetail>(`/v1/customer-orders/${customerOrderId}`)
}

/**
 * Skips an order: it is marked canceled and will not become a sales order. Named for what a person
 * does, as on the order confirmation side, where the route is also called cancel.
 */
export async function skipCustomerOrder(customerOrderId: string): Promise<AckResponse> {
  return api.post<AckResponse>(`/v1/customer-orders/${customerOrderId}/cancel`)
}

/** Takes an order out of the queue for somebody to deal with outside the product. */
export async function markManualHandlingCustomerOrder(
  customerOrderId: string,
): Promise<AckResponse> {
  return api.post<AckResponse>(`/v1/customer-orders/${customerOrderId}/manual-handling`)
}

/**
 * Saves the validation view's edits. Partial by design: only what was changed is sent, so a save
 * cannot overwrite a field somebody else changed in the meantime.
 *
 * Answers 204 with no body — the page refetches the order rather than trusting a copy of it.
 */
export async function saveCustomerOrderDraft(
  customerOrderId: string,
  body: CustomerOrderDraft,
): Promise<void> {
  await api.patch<void>(`/v1/customer-orders/${customerOrderId}`, body)
}

/** Adds a line to a customer order, for an article its document did not carry. */
export async function createCustomerOrderRow(
  customerOrderId: string,
  body: NewCustomerOrderRow,
): Promise<AckResponse> {
  return api.post<AckResponse>(`/v1/customer-orders/${customerOrderId}/rows`, body)
}

/**
 * Drops a line from a customer order. The row is marked deleted rather than removed, so what the
 * order once said is still readable in the log, but nothing in the product offers it back.
 */
export async function deleteCustomerOrderRow(
  customerOrderId: string,
  rowId: string,
): Promise<AckResponse> {
  return api.delete<AckResponse>(`/v1/customer-orders/${customerOrderId}/rows/${rowId}`)
}

/**
 * Saves the draft and creates that order in the ERP, as one action: what is sent has to be what was
 * on screen. The same body a draft save takes, for the same reason.
 *
 * Once this succeeds the order has been sent and can no longer be edited.
 */
/**
 * Creates the order in the ERP as it stands. It carries no body: saving is the draft endpoint's
 * job, and the view saves before it calls this.
 */
export async function createCustomerOrderSalesOrder(
  customerOrderId: string,
): Promise<CreateSalesOrderResult> {
  return api.post<CreateSalesOrderResult>(
    `/v1/customer-orders/${customerOrderId}/create-sales-order`,
    {},
  )
}

/** The workflow's own settings — what every customer without an override is validated against. */
export async function getWorkflowSalesOrderSettings(
  workflowId: string,
): Promise<SalesOrderSettings> {
  return api.get<SalesOrderSettings>(`/v1/workflows/${workflowId}/sales-order/settings`)
}

export async function saveWorkflowSalesOrderSettings(
  workflowId: string,
  body: UpdateSalesOrderSettingsRequest,
): Promise<SalesOrderSettings> {
  return api.post<SalesOrderSettings>(`/v1/workflows/${workflowId}/sales-order/settings`, body)
}

/** A customer's own settings. 404 means they have none and follow the workflow's. */
export async function getCustomerSalesOrderSettings(
  workflowId: string,
  customerId: string,
): Promise<SalesOrderSettings> {
  return api.get<SalesOrderSettings>(
    `/v1/workflows/${workflowId}/sales-order/customers/${customerId}/settings`,
  )
}

export async function saveCustomerSalesOrderSettings(
  workflowId: string,
  customerId: string,
  body: UpdateSalesOrderSettingsRequest,
): Promise<SalesOrderSettings> {
  return api.post<SalesOrderSettings>(
    `/v1/workflows/${workflowId}/sales-order/customers/${customerId}/settings`,
    body,
  )
}

export async function deleteCustomerSalesOrderSettings(
  workflowId: string,
  customerId: string,
): Promise<void> {
  await api.delete<void>(
    `/v1/workflows/${workflowId}/sales-order/customers/${customerId}/settings`,
  )
}

export async function getOrderConfirmationDetail(
  orderConfirmationId: string,
): Promise<OrderConfirmationDetail> {
  return api.get<OrderConfirmationDetail>(`/v1/order-confirmations/${orderConfirmationId}`)
}

export async function searchPurchaseOrders(query: string): Promise<PurchaseOrderSearchResults> {
  const q = encodeURIComponent(query.trim())
  return api.get<PurchaseOrderSearchResults>(`/v1/purchase-orders/search?query=${q}`)
}

export async function linkOrderConfirmationToPurchaseOrder(
  orderConfirmationId: string,
  purchaseOrderId: string,
): Promise<AckResponse> {
  return api.post<AckResponse>(
    `/v1/order-confirmations/${orderConfirmationId}/link-purchase-order`,
    { purchase_order_id: purchaseOrderId },
  )
}

export async function markManualHandlingOrderConfirmation(orderConfirmationId: string): Promise<AckResponse> {
  return api.post<AckResponse>(`/v1/order-confirmations/${orderConfirmationId}/manual-handling`)
}

export async function cancelOrderConfirmation(orderConfirmationId: string): Promise<AckResponse> {
  return api.post<AckResponse>(`/v1/order-confirmations/${orderConfirmationId}/cancel`)
}

export async function validateOrderConfirmation(orderConfirmationId: string): Promise<ValidateAckResponse> {
  return api.post<ValidateAckResponse>(`/v1/order-confirmations/${orderConfirmationId}/validate`)
}

export interface CreatedManualOrderConfirmation {
  id: string
}

/**
 * Creates an empty / copied manual order confirmation for the PO (no body).
 * Backend requires no open or initialized OCs on the purchase order.
 */
export async function createManualOrderConfirmation(
  purchaseOrderId: string,
): Promise<CreatedManualOrderConfirmation> {
  return api.post<CreatedManualOrderConfirmation>(
    `/v1/purchase-orders/${purchaseOrderId}/order-confirmations`,
  )
}

export type UpdateOrderConfirmationHeadPayload = Partial<{
  customer_order_number: string
  supplier_order_number: string
  supplier_name: string
  supplier_contact_person: string
  invoice_address_company_name: string
  invoice_address_street: string
  invoice_address_house_number: string
  invoice_address_postal_code: string
  invoice_address_city: string
  invoice_address_country: string
  invoice_address_additional_information: string
  delivery_address_company_name: string
  delivery_address_street: string
  delivery_address_house_number: string
  delivery_address_postal_code: string
  delivery_address_city: string
  delivery_address_country: string
  delivery_address_additional_information: string
  payment_terms: string
  delivery_terms: string
  delivery_method: string
}>

export async function updateOrderConfirmationHead(
  orderConfirmationId: string,
  body: UpdateOrderConfirmationHeadPayload,
): Promise<void> {
  await api.patch<void>(`/v1/order-confirmations/${orderConfirmationId}/head`, body)
}

/** Send only the fields that changed — each PATCH records one logical edit / audit entry. */
export type UpdateConfirmedQuantityPayload = {
  quantity?: number
  delivery_date?: string
  arrival_date?: string
}

/**
 * Updates one or more fields on a confirmed-quantity line (partial PATCH supported).
 */
export async function updateOrderConfirmationConfirmedQuantity(
  confirmedQuantityId: string,
  body: UpdateConfirmedQuantityPayload,
): Promise<void> {
  await api.patch<void>(`/v1/confirmed-quantities/${confirmedQuantityId}`, body)
}

export async function unpairOrderConfirmationRow(ocRowId: string): Promise<void> {
  await api.delete<void>(`/v1/order-confirmation-rows/${ocRowId}/match`)
}

export async function pairOrderConfirmationRow(
  ocRowId: string,
  purchaseOrderRowId: string,
): Promise<void> {
  await api.post<void>(`/v1/order-confirmation-rows/${ocRowId}/match`, {
    purchase_order_row_id: purchaseOrderRowId,
  })
}

export async function duplicateOrderConfirmationRow(ocRowId: string): Promise<{ id: string }> {
  return api.post<{ id: string }>(`/v1/order-confirmation-rows/${ocRowId}/duplicate`)
}

export async function splitOrderConfirmationRowQuantities(ocRowId: string): Promise<void> {
  await api.post<void>(`/v1/order-confirmation-rows/${ocRowId}/split-quantities`)
}

export async function deleteOrderConfirmationRow(ocRowId: string): Promise<void> {
  await api.delete<void>(`/v1/order-confirmation-rows/${ocRowId}`)
}

export async function createOrderConfirmationRow(
  ocId: string,
  body: { purchase_order_row_id: string; quantity: number; delivery_date: string },
): Promise<{ id: string }> {
  return api.post<{ id: string }>(`/v1/order-confirmations/${ocId}/rows`, body)
}

// --- Admin tool -----------------------------------------------------------

export interface AdminOrganizationListParams {
  page?: number
  q?: string
  scope?: 'subtree' | 'all'
  hasCustomerWorkflow?: boolean
  type?: 'standard' | 'partner'
}

function buildAdminOrganizationListParams(params: AdminOrganizationListParams = {}): string {
  const qs = new URLSearchParams()
  if (params.page && params.page > 1) qs.set('page', String(params.page))
  if (params.q) qs.set('q', params.q)
  if (params.scope) qs.set('scope', params.scope)
  if (params.hasCustomerWorkflow) qs.set('has_customer_workflow', 'true')
  if (params.type) qs.set('type', params.type)
  const str = qs.toString()
  return str ? `?${str}` : ''
}

export async function getAdminOrganizations(
  params?: AdminOrganizationListParams,
): Promise<PaginatedResponse<AdminOrganizationListItem>> {
  return api.get<PaginatedResponse<AdminOrganizationListItem>>(
    `/v1/admin/organizations${buildAdminOrganizationListParams(params)}`,
  )
}

export async function getAdminOrganization(organizationId: string): Promise<AdminOrganizationDetail> {
  return api.get<AdminOrganizationDetail>(`/v1/admin/organizations/${organizationId}`)
}

export interface CreateAdminOrganizationPayload {
  name: string
  tenant_id: string
  type?: 'standard' | 'partner'
}

export async function createAdminOrganization(
  body: CreateAdminOrganizationPayload,
): Promise<AdminOrganizationDetail> {
  return api.post<AdminOrganizationDetail>('/v1/admin/organizations', body)
}

export interface UpdateAdminOrganizationPayload {
  name: string
  /** Root only. Omit to leave alone. */
  type?: 'standard' | 'partner'
  /** Root only. Moves the organization to a different Microsoft tenant. */
  tenant_id?: string
}

export async function updateAdminOrganization(
  organizationId: string,
  body: UpdateAdminOrganizationPayload,
): Promise<AdminOrganizationDetail> {
  return api.patch<AdminOrganizationDetail>(`/v1/admin/organizations/${organizationId}`, body)
}

export async function getAdminOrganizationWorkflows(
  organizationId: string,
  audience: 'customer' | 'test' | 'all' = 'all',
): Promise<AdminWorkflow[]> {
  return api.get<AdminWorkflow[]>(
    `/v1/admin/organizations/${organizationId}/workflows?audience=${audience}`,
  )
}

export interface CreateAdminWorkflowPayload {
  name: string
  type: string
  audience: WorkflowAudience
}

export async function createAdminWorkflow(
  organizationId: string,
  body: CreateAdminWorkflowPayload,
): Promise<AdminWorkflow> {
  return api.post<AdminWorkflow>(`/v1/admin/organizations/${organizationId}/workflows`, body)
}

export interface UpdateAdminWorkflowPayload {
  name: string
  status?: WorkflowStatus
  start_mode?: WorkflowStartMode
}

export async function updateAdminWorkflow(
  organizationId: string,
  workflowId: string,
  body: UpdateAdminWorkflowPayload,
): Promise<AdminWorkflow> {
  return api.patch<AdminWorkflow>(
    `/v1/admin/organizations/${organizationId}/workflows/${workflowId}`,
    body,
  )
}

const workflowPath = (organizationId: string, workflowId: string) =>
  `/v1/admin/organizations/${organizationId}/workflows/${workflowId}`

export async function getAdminWorkflowFlow(
  organizationId: string,
  workflowId: string,
): Promise<WorkflowFlow | null> {
  return api.get<WorkflowFlow | null>(`${workflowPath(organizationId, workflowId)}/flow`)
}

export async function updateAdminWorkflowFlow(
  organizationId: string,
  workflowId: string,
  flow: WorkflowFlow,
): Promise<AdminWorkflow> {
  return api.put<AdminWorkflow>(`${workflowPath(organizationId, workflowId)}/flow`, flow)
}

export async function getAdminWorkflowEmailSettings(
  organizationId: string,
  workflowId: string,
): Promise<AdminWorkflowEmailSettings> {
  return api.get<AdminWorkflowEmailSettings>(
    `${workflowPath(organizationId, workflowId)}/email-settings`,
  )
}

export type UpdateEmailSettingsPayload = Omit<
  AdminWorkflowEmailSettings,
  'last_connected_at' | 'last_connection_attempt_at' | 'connection_state'
> & {
  /** Write-only — GET never returns it. Omit, or send '', to leave it as it is. */
  client_secret?: string
}

export async function updateAdminWorkflowEmailSettings(
  organizationId: string,
  workflowId: string,
  body: UpdateEmailSettingsPayload,
): Promise<AdminWorkflowEmailSettings> {
  return api.put<AdminWorkflowEmailSettings>(
    `${workflowPath(organizationId, workflowId)}/email-settings`,
    body,
  )
}

export async function getAdminWorkflowErpSettings(
  organizationId: string,
  workflowId: string,
): Promise<AdminWorkflowErpSettings> {
  return api.get<AdminWorkflowErpSettings>(
    `${workflowPath(organizationId, workflowId)}/erp-settings`,
  )
}

export interface UpdateErpSettingsPayload {
  provider: string
  credentials?: {
    safe: Record<string, string | boolean>
    secrets: Record<string, string>
  }
}

export async function updateAdminWorkflowErpSettings(
  organizationId: string,
  workflowId: string,
  body: UpdateErpSettingsPayload,
): Promise<AdminWorkflowErpSettings> {
  return api.put<AdminWorkflowErpSettings>(
    `${workflowPath(organizationId, workflowId)}/erp-settings`,
    body,
  )
}

export async function triggerAdminWorkflowFetchEmails(
  organizationId: string,
  workflowId: string,
): Promise<void> {
  await api.post<void>(`${workflowPath(organizationId, workflowId)}/fetch-emails`)
}

/** Block names the admin tool has a settings shape for — the "which block" dropdown. */
export async function getAdminBlocksWithSettings(): Promise<string[]> {
  return api.get<string[]>('/v1/admin/blocks')
}

/** A block's settings at their Go zero value — the template a new row starts from. */
export async function getAdminBlockDefaultSettings(blockName: string): Promise<unknown> {
  return api.get<unknown>(`/v1/admin/blocks/${blockName}/default-settings`)
}

export async function getAdminWorkflowBlockSettings(
  organizationId: string,
  workflowId: string,
): Promise<AdminWorkflowBlockSettings[]> {
  return api.get<AdminWorkflowBlockSettings[]>(
    `${workflowPath(organizationId, workflowId)}/block-settings`,
  )
}

export async function createAdminWorkflowBlockSettings(
  organizationId: string,
  workflowId: string,
  body: { block_name: string; settings: unknown },
): Promise<AdminWorkflowBlockSettings> {
  return api.post<AdminWorkflowBlockSettings>(
    `${workflowPath(organizationId, workflowId)}/block-settings`,
    body,
  )
}

export async function updateAdminWorkflowBlockSettings(
  organizationId: string,
  workflowId: string,
  blockName: string,
  settings: unknown,
): Promise<AdminWorkflowBlockSettings> {
  return api.put<AdminWorkflowBlockSettings>(
    `${workflowPath(organizationId, workflowId)}/block-settings/${blockName}`,
    { settings },
  )
}

export async function deleteAdminWorkflowBlockSettings(
  organizationId: string,
  workflowId: string,
  blockName: string,
): Promise<void> {
  await api.delete<void>(`${workflowPath(organizationId, workflowId)}/block-settings/${blockName}`)
}

export interface AdminWorkflowListParams {
  page?: number
  q?: string
  organizationId?: string
  type?: string
  audience?: string
  status?: string
  readiness?: string
  scope?: 'subtree' | 'all'
}

export async function getAdminWorkflows(
  params: AdminWorkflowListParams = {},
): Promise<PaginatedResponse<AdminWorkflowListItem>> {
  const qs = new URLSearchParams()
  if (params.page && params.page > 1) qs.set('page', String(params.page))
  if (params.q) qs.set('q', params.q)
  if (params.organizationId) qs.set('organization_id', params.organizationId)
  if (params.type) qs.set('type', params.type)
  if (params.audience) qs.set('audience', params.audience)
  if (params.status) qs.set('status', params.status)
  if (params.readiness) qs.set('readiness', params.readiness)
  if (params.scope) qs.set('scope', params.scope)
  const str = qs.toString()
  return api.get<PaginatedResponse<AdminWorkflowListItem>>(
    `/v1/admin/workflows${str ? `?${str}` : ''}`,
  )
}
